import { useEffect, useRef, useState } from "preact/hooks";
import { registerLogoTap } from "../devsheet-hooks";
import { loadEnvelopes, onPeonSpeech, type PlayingClip } from "../audio";
import { chooseGazeDirection, type GazeDirection } from "../peon-gaze";
import { envelopeStateAt, ENVELOPE_FPS } from "../../shared/peonEnvelope.ts";

const ENVELOPE_TICK_MS = 1000 / ENVELOPE_FPS;

const SHEET_URL = "/ui/peon-sheet@3x.png";
const SHEET_W = 828;
const SHEET_H = 38;
const CELL_W = 46;

// Matches public/ui/peon-sheet.json exactly: index = eye * mouths.length + mouth.
const EYES = ["center", "left", "right", "downLeft", "downRight", "blink"] as const;
type EyeName = (typeof EYES)[number];
const MOUTH_COUNT = 3;
const BLINK_EYE: EyeName = "blink";

function cellIndex(eye: EyeName, mouth: number): number {
  return EYES.indexOf(eye) * MOUTH_COUNT + mouth;
}

const FOCUSABLE_SELECTOR = "input, textarea, select, [contenteditable], [contenteditable='true']";

function randomBetween(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/** Guards a MediaQueryList lookup: some environments have no matchMedia at all. */
function safeMatchMedia(query: string): MediaQueryList | null {
  try {
    return typeof matchMedia === "function" ? matchMedia(query) : null;
  } catch {
    return null;
  }
}

/**
 * The always-on-screen animated mascot (src/components/Shell.tsx's Header,
 * behind the `livePeon` flag): idle blink/breathing-bob/glance, gaze at
 * whatever form field is focused, and a speaking mouth driven by
 * src/audio.ts's onPeonSpeech. Purely decorative (aria-hidden) and drawn
 * with direct DOM writes rather than Preact state so a blink or a mouth
 * frame never triggers a re-render.
 *
 * Robustness: every DOM/rect read is guarded, every timer/listener/rAF is
 * cleared on unmount, and a sheet that fails to load falls back to the same
 * still image the flag would otherwise be replacing, so this can never be
 * the thing that crashes the app.
 */
export function LivePeon() {
  const elRef = useRef<HTMLDivElement | null>(null);
  const [sheetFailed, setSheetFailed] = useState(false);

  // Probe the sheet once, independently of the element's own background-image
  // load, so a 404 (or any load error) can swap to the fallback <img>.
  useEffect(() => {
    let cancelled = false;
    try {
      const probe = new Image();
      probe.onerror = () => {
        if (!cancelled) setSheetFailed(true);
      };
      probe.src = SHEET_URL;
    } catch {
      setSheetFailed(true);
    }
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (sheetFailed) return;
    const el = elRef.current;
    if (!el) return;

    let destroyed = false;
    let restingEye: EyeName = "center";
    let mouth = 0;
    let blinking = false;
    let bobOn = false;

    const reduceMotionQuery = safeMatchMedia("(prefers-reduced-motion: reduce)");
    const reducedMotion = () => !!reduceMotionQuery?.matches;

    function paint() {
      if (destroyed) return;
      try {
        const eye = blinking ? BLINK_EYE : restingEye;
        const idx = cellIndex(eye, mouth);
        el!.style.backgroundPosition = `-${idx * CELL_W}px 0`;
        el!.style.transform = bobOn ? "translateY(1px)" : "translateY(0)";
      } catch {
        // A style write should never throw, but a paint failure must never take the app down.
      }
    }

    // ---------- idle timers: blink, breathing bob, idle glance ----------
    // Tracked in one bag so a page-hide (or unmount) can clear all of them
    // at once; each entry removes itself once it fires.
    const idleTimers = new Set<ReturnType<typeof setTimeout>>();
    function after(ms: number, fn: () => void): void {
      const id = setTimeout(() => {
        idleTimers.delete(id);
        if (!destroyed) fn();
      }, ms);
      idleTimers.add(id);
    }
    function clearIdleTimers(): void {
      for (const id of idleTimers) clearTimeout(id);
      idleTimers.clear();
    }

    let bobInterval: ReturnType<typeof setInterval> | undefined;
    function startBob(): void {
      clearInterval(bobInterval);
      bobInterval = undefined;
      if (reducedMotion()) return;
      bobInterval = setInterval(() => {
        bobOn = !bobOn;
        paint();
      }, 1200);
    }

    let focusActive = false;

    function scheduleBlink(): void {
      after(randomBetween(2500, 6000), runBlinkCycle);
    }
    function playBlink(onDone: () => void): void {
      blinking = true;
      paint();
      after(120, () => {
        blinking = false;
        paint();
        onDone();
      });
    }
    function runBlinkCycle(): void {
      playBlink(() => {
        if (Math.random() < 0.25) {
          after(150, () => playBlink(scheduleBlink));
        } else {
          scheduleBlink();
        }
      });
    }

    function scheduleGlance(): void {
      after(randomBetween(8000, 15000), runGlance);
    }
    function runGlance(): void {
      if (reducedMotion() || focusActive) {
        scheduleGlance();
        return;
      }
      restingEye = Math.random() < 0.5 ? "left" : "right";
      paint();
      after(1000, () => {
        if (!focusActive) restingEye = "center";
        paint();
        scheduleGlance();
      });
    }

    function handleVisibility(): void {
      if (document.visibilityState === "hidden") {
        clearIdleTimers();
        clearInterval(bobInterval);
        bobInterval = undefined;
      } else {
        scheduleBlink();
        scheduleGlance();
        startBob();
      }
    }
    document.addEventListener("visibilitychange", handleVisibility);
    handleVisibility();

    // ---------- gaze at the focused field ----------
    let focusHoldTimer: ReturnType<typeof setTimeout> | undefined;
    function onFocusIn(e: FocusEvent): void {
      try {
        const target = e.target;
        if (!(target instanceof Element) || typeof target.matches !== "function") return;
        if (!target.matches(FOCUSABLE_SELECTOR)) return;
        const peonRect = el!.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const direction: GazeDirection = chooseGazeDirection(
          { x: peonRect.left + peonRect.width / 2, y: peonRect.top + peonRect.height / 2 },
          { x: targetRect.left + targetRect.width / 2, y: targetRect.top + targetRect.height / 2 },
        );
        focusActive = true;
        restingEye = direction;
        paint();
        clearTimeout(focusHoldTimer);
        focusHoldTimer = setTimeout(() => {
          if (destroyed) return;
          focusActive = false;
          restingEye = "center";
          paint();
        }, 2000);
      } catch {
        // A layout read failing (e.g. a detached node) must never break the page; just skip this aim.
      }
    }
    document.addEventListener("focusin", onFocusIn);

    // ---------- speaking: driven by src/audio.ts's onPeonSpeech ----------
    let speakingCleanup: (() => void) | null = null;
    function stopSpeaking(): void {
      speakingCleanup?.();
      speakingCleanup = null;
      mouth = 0;
      paint();
    }
    function startSpeaking(audio: PlayingClip, dir: string, name: string): void {
      stopSpeaking();
      let cancelled = false;
      let envelope: number[] | null = null;
      // A timer, not requestAnimationFrame: the mouth only has to match audio.currentTime at the
      // envelope's own 20fps resolution, and unlike rAF a timer keeps firing even when the page
      // isn't compositing visible frames (some embedded/automated contexts never tick rAF at all),
      // so the mouth still tracks a genuinely playing clip.
      let tickTimer: ReturnType<typeof setInterval> | undefined;
      let flapTimer: ReturnType<typeof setTimeout> | undefined;

      loadEnvelopes(dir)
        .then((map) => {
          if (cancelled || !map) return;
          const found = map[name];
          envelope = Array.isArray(found) && found.length > 0 ? found : null;
        })
        .catch(() => {
          envelope = null;
        });

      function finished(): boolean {
        return cancelled || destroyed || audio.paused || audio.ended;
      }
      function tick(): void {
        if (finished()) {
          stopSpeaking();
          return;
        }
        if (envelope) {
          mouth = envelopeStateAt(envelope, audio.currentTime);
          paint();
        }
      }
      function flap(): void {
        if (finished()) return;
        if (!envelope) {
          mouth = Math.floor(Math.random() * MOUTH_COUNT);
          paint();
        }
        flapTimer = setTimeout(flap, randomBetween(90, 140));
      }
      const onAudioStop = () => stopSpeaking();
      audio.addEventListener("ended", onAudioStop);
      audio.addEventListener("pause", onAudioStop);

      tickTimer = setInterval(tick, ENVELOPE_TICK_MS);
      flapTimer = setTimeout(flap, randomBetween(90, 140));

      speakingCleanup = () => {
        cancelled = true;
        clearInterval(tickTimer);
        clearTimeout(flapTimer);
        audio.removeEventListener("ended", onAudioStop);
        audio.removeEventListener("pause", onAudioStop);
      };
    }
    const unsubscribeSpeech = onPeonSpeech((audio, dir, name) => {
      try {
        startSpeaking(audio, dir, name);
      } catch {
        // A bad speech event must never break the mascot or the app.
      }
    });

    paint();

    return () => {
      destroyed = true;
      clearIdleTimers();
      clearInterval(bobInterval);
      clearTimeout(focusHoldTimer);
      document.removeEventListener("visibilitychange", handleVisibility);
      document.removeEventListener("focusin", onFocusIn);
      stopSpeaking();
      unsubscribeSpeech();
    };
  }, [sheetFailed]);

  if (sheetFailed) {
    return <img class="topbar-logo" src="/ui/peon-original@3x.png" alt="" aria-hidden="true" onClick={registerLogoTap} />;
  }
  return (
    <div
      ref={elRef}
      class="live-peon"
      aria-hidden="true"
      onClick={registerLogoTap}
      style={{ backgroundImage: `url(${SHEET_URL})`, backgroundSize: `${SHEET_W}px ${SHEET_H}px`, backgroundPosition: "0 0" }}
    />
  );
}
